import React, { ChangeEvent, useState, useEffect } from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import ArrowLeft from '@material-ui/icons/ArrowLeft';
import ErrorOutlineRoundedIcon from '@material-ui/icons/ErrorOutlineRounded';
import CalendarPicker from './EditUserDatePicker.component';
import { verifyDuplicatePesel, verifyDuplicateMail } from '../../services/validation/validation.service';
import { validateFormatEmailInput, validatePhoneNumberInput } from '../../services/validation/validationUser';
import { isPeselValid } from '../../utils/peselValidator';
import { AccountTypeName, AccountTypeEnum, UserM, accountTypes } from '../../models/user.types';
import { useStyles, styles, CancelButton, ConfirmButton } from './EditUserForm.styles';
import { useTranslation } from 'react-i18next';
import { getCommitteesList } from '../../pages/SampleAssessmentRepo/Helpers/AccountTypeRendering';
import CloseButton from '../buttons/CloseButtton.component';
import { committeesArray } from '../../models/committees.types';
import { getLocalISODate } from '../../utils/dateFunctionUtils';
import { FormControlLabel } from '@mui/material';
import { CheckboxOne } from '../CheckboxOne/CheckboxOne.component';
import { blockIconForInput } from '../EditSession/BlockItemForInput';

function getAccountType(): readonly AccountTypeName[] {
  return accountTypes;
}

interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, ...other } = props;
  return (
    <MuiDialogTitle className={classes.root} {...other}>
      {children}
    </MuiDialogTitle>
  );
});

interface EditUserFormProps {
  isExaminerList?: boolean;
  userArg: UserM;
  goToDataView: () => void;
  closeModal: () => void;
  updateUser: (
    id: string,
    firstName: string,
    lastName: string,
    phone: string,
    phoneHidden: boolean,
    email: string,
    accountTypeId: AccountTypeEnum,
    examCommiteeId: number,
    expirationDate: string,
    pesel: string,
  ) => void;
}

const EditUserForm = (props: EditUserFormProps) => {
  const { t } = useTranslation();

  const commitees = getCommitteesList();
  const accountTypes = getAccountType();
  const [name, setName] = useState<string>(props.userArg.firstName);
  const [surname, setSurname] = useState<string>(props.userArg.lastName);
  const [pesel, setPesel] = useState<string>(props.userArg.pesel);
  const [validPesel, setValidPesel] = useState<boolean>(true);
  const [uniquePesel, setUniquePesel] = useState<boolean>(true);
  const [phone, setPhone] = useState<string>(props.userArg.phone);
  const [phoneHidden, setPhoneHidden] = useState<boolean>(props.userArg.phoneHidden);
  const [validPhone, setValidPhone] = useState<boolean>(true);
  const [mail, setMail] = useState<string>(props.userArg.email);
  const [validMail, setValidMail] = useState<boolean>(true);
  const [uniqueMail, setUniqueMail] = useState<boolean>(true);
  const [accType, setAccType] = useState<AccountTypeEnum>(props.userArg.accountTypeId);
  const [commitee, setCommitee] = useState(props.userArg.examCommitteeId);
  const [expDate, setExpDate] = useState<Date | null>(props.userArg.expirationDate);
  const [isDateValid, setIsDateValid] = useState<boolean>(false)
  const classes = useStyles();
  const [isObjectUnchanged, setIsObjectUnchanged] = useState<boolean>(true);

  const correctValidations =
    Number(validPesel) + Number(validPhone) + Number(uniquePesel) + Number(validMail) + Number(uniqueMail);

  const validatePhoneNumber = (phone: string) => {
    const regexPhone = validatePhoneNumberInput(phone);
    if (phone.length === 0 && !regexPhone) {
      setValidPhone(true);
    } else {
      !regexPhone ? setValidPhone(false) : setValidPhone(true);
    }
  };

  const UniquePeselValidation = () => {
    if (pesel === props.userArg.pesel && validPesel) {
      setUniquePesel(true);
    } else if (validPesel && pesel.length != 0) {
      verifyDuplicatePesel(pesel).then((result) => {
        setUniquePesel(result);
      });
    } else if (pesel.length === 0) {
      setUniquePesel(true);
    }
  };

  const validateFormatEmail = (email: string) => {
    const regexp = validateFormatEmailInput(email);
    if (!regexp) {
      setValidMail(false);
    } else {
      setValidMail(true);
    }
  };

  const handlePhoneFocus = () => {
    validatePhoneNumber(phone);
  };
  const handleEmailFocus = () => {
    validateFormatEmail(mail);
    if (mail === props.userArg.email && validMail) {
      setUniqueMail(true);
    } else if (validMail && mail.length != 0) {
      verifyDuplicateMail(mail).then((result) => {
        setUniqueMail(result);
      });
    } else if (mail.length === 0) {
      setUniqueMail(true);
      setValidMail(true);
    }
  };

  const handlePeselFocus = () => {
    if (pesel.length === 11 && isPeselValid(pesel)) {
      setValidPesel(true);
      UniquePeselValidation();
    } else if (!pesel.length) {
      setValidPesel(true);
    } else {
      setValidPesel(false);
    }
  };
  useEffect(() => {
    if (
      props.userArg.firstName === name &&
      props.userArg.lastName === surname &&
      props.userArg.phone === phone &&
      props.userArg.phoneHidden === phoneHidden &&
      props.userArg.email === mail &&
      props.userArg.accountTypeId === accType &&
      props.userArg.examCommitteeId === commitee &&
      props.userArg.expirationDate === expDate &&
      props.userArg.pesel === pesel
    ) {
      setIsObjectUnchanged(true);
    } else {
      setIsObjectUnchanged(false);
    }
  }, [props.userArg, name, surname, phone, mail, accType, commitees, expDate, pesel]);

  const handleCommitee = (e: any) => {
    setCommitee(e);
  };

  const handleAccType = (e: any) => {
    setAccType(e);
  };

  const updateUser = () => {
    props.updateUser(
      props.userArg.id.toString(),
      name,
      surname,
      phone,
      phoneHidden,
      mail,
      accType,
      commitee,
      pesel,
      expDate ? expDate.toString() : '',
    );
  };

  console.log(commitee)

  const dateToParent = (_date: Date | null) => {
    let expirationDate = _date;
    if (expirationDate) {
      try {
        expirationDate = new Date(getLocalISODate(expirationDate));
      } catch {}
      expirationDate.setUTCHours(23, 59);
    }
    setExpDate(expirationDate);
  };

  const disableButton = () => {
    const result =
      !validPhone ||
      !validPesel ||
      !validMail ||
      mail.length === 0 ||
      !uniqueMail ||
      !uniquePesel ||
      name.length === 0 ||
      surname.length === 0 ||
      mail.length === 0 ||
      pesel.length === 0 ||
      phone.length === 0 ||
      isObjectUnchanged || isDateValid
    return result;
  };
  return (
    <>
      <DialogTitle id="customized-dialog-title" onClose={props.closeModal}>
        <Grid container>
          <Grid item xs={6}>
            <div className={classes.modalTitle}>
              {props.userArg.firstName} {props.userArg.lastName}
            </div>
          </Grid>
          <Grid item xs={6}>
            <CloseButton onClick={props.goToDataView} />
          </Grid>
        </Grid>
        <Grid>
          <div className={classes.backButton} onClick={props.goToDataView}>
            <ArrowLeft className={classes.backArrowIcon} />
            Powrót
          </div>
          {correctValidations < 4 ? (
            <div className={classes.errorMessage}>
              <ErrorOutlineRoundedIcon className={classes.errorIcon} /> Formularz zawiera błędy, spróbuj wprowadzić dane
              ponownie
            </div>
          ) : undefined}
        </Grid>
        <Grid>
          <div className={classes.modalSubTitle}>Edytuj dane</div>
        </Grid>
        <Grid container>
          <Grid item xs={6}>
            <TextField
              value={name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value.trimStart())}
              className={classes.leftFormInput}
              id="imię"
              label="Imię"
              aria-label="imię"
              placeholder="Imię"
              variant="outlined"
              inputProps={{
                style: {
                  padding: '15.5px',
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              value={surname}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSurname(e.target.value.trimStart())}
              className={classes.rightFormInput}
              id="nazwisko"
              label="Nazwisko"
              aria-label="nazwisko"
              placeholder="Nazwisko"
              variant="outlined"
              inputProps={{
                style: {
                  padding: '15.5px',
                },
              }}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={6}>
            <TextField
              error={!validPesel || !uniquePesel}
              className={classes.leftFormInput}
              helperText={
                validPesel
                  ? uniquePesel
                    ? undefined
                    : 'Podany numer PESEL jest już wykorzystany'
                  : 'Podany numer PESEL jest nieprawidłowy'
              }
              style={validPesel ? (uniquePesel ? undefined : { marginBottom: '47px' }) : { marginBottom: '47px' }}
              value={pesel}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                setPesel(e.target.value.trimStart())
              }
              onBlur={handlePeselFocus}
              id="pesel"
              label="PESEL"
              aria-label="pesel"
              placeholder="PESEL"
              variant="outlined"
              inputProps={{
                style: {
                  padding: '15.5px',
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              error={!validPhone}
              helperText={validPhone ? undefined : 'Podany numer telefonu jest nieprawidłowy'}
              style={validPhone ? undefined : { marginBottom: '47px' }}
              value={phone}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPhone(e.target.value.trimStart())}
              onBlur={handlePhoneFocus}
              className={classes.rightFormInput}
              id="nr telefonu"
              label="Nr telefonu"
              aria-label="nr telefonu"
              placeholder="Nr telefonu"
              variant="outlined"
              inputProps={{
                style: {
                  padding: '15.5px',
                },
              }}
            />
          </Grid>
        </Grid>
        <Grid container style={{ margin:'-20px 0 15px 0' }}>
          <Grid item xs={6}></Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <CheckboxOne
                  id="PhoneHidden"
                  name={'PhoneHidden'}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setPhoneHidden(e.target.checked)}
                  checked={phoneHidden}
                  value={phoneHidden}
                />
              }
              label="Ukryj numer telefonu"
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={6}>
            <TextField
              error={!validMail || !uniqueMail}
              helperText={
                validMail
                  ? uniqueMail
                    ? undefined
                    : 'Podany adres e-mail jest już wykorzystany'
                  : 'Podany adres e-mail jest nieprawidłowy'
              }
              style={validMail ? (uniqueMail ? undefined : { marginBottom: '47px' }) : { marginBottom: '47px' }}
              value={mail}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setMail(e.target.value.trimStart())}
              onBlur={handleEmailFocus}
              className={classes.leftFormInput}
              id="e-mail"
              label="E-mail"
              aria-label="e-mail"
              placeholder="E-mail"
              variant="outlined"
              inputProps={{
                style: {
                  padding: '15.5px',
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="outlined" className={classes.accTypeInput}>
              <InputLabel id="typ konta">Typ konta</InputLabel>
              <Select
                value={accType}
                name={accType.toString()}
                onChange={(e) => handleAccType(e.target.value)}
                id="typ konta"
                label="Typ konta"
                aria-label="typ konta"
                disabled={accType === AccountTypeEnum.Examiner}
                startAdornment={blockIconForInput(accType === AccountTypeEnum.Examiner)}
                SelectDisplayProps={{
                  style: {
                    padding: '15.5px',
                  },
                }}
              >
                {accountTypes.map((name, index) => (
                  <MenuItem disableRipple className={classes.menuItem} key={index} value={index + 1}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={6}>
            <FormControl id='komisja' variant="outlined" className={classes.commiteeInput}>
              <InputLabel id="komisja">Komisja*</InputLabel>
              <Select
                value={commitee ?? ''}
                onChange={(e) => handleCommitee(e.target.value)}
                id="komisja"
                label="Komisja"
                aria-label="komisja"
                SelectDisplayProps={{
                  style: {
                    padding: '15.5px',
                  },
                }}
              >
                {committeesArray.map((item, index) => (
                  <MenuItem disableRipple className={classes.menuItem} key={index} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <CalendarPicker dateArg={expDate ? new Date(expDate) : null} dateToParent={dateToParent} isDateValid={setIsDateValid}></CalendarPicker>
          </Grid>
        </Grid>
        <Grid container style={{ marginBottom: '65px' }}>
          <Grid item xs={12}>
            <CancelButton onClick={props.goToDataView}>Anuluj</CancelButton>
            <ConfirmButton disabled={disableButton()} onClick={updateUser}>
              Zapisz zmiany
            </ConfirmButton>
          </Grid>
        </Grid>
      </DialogTitle>
    </>
  );
};

export default EditUserForm;
